import { Loader2 } from "lucide-react";
import { SLOW_DELAY_MS, useDelayedFlag } from "@/lib/loading";
import { cn } from "@/lib/utils";

/**
 * Reassurance for a wait that has gone on long enough to look like a failure.
 *
 * A skeleton is a good answer to "is something coming?" for about two seconds.
 * Past that it stops reading as loading and starts reading as stuck, and the
 * natural next move is a reload — which throws away the request that was about
 * to succeed. This says the quiet part instead.
 *
 * Cold starts are the usual cause: an idle Worker plus a cold Hyperdrive
 * connection is genuinely slow on the first request, and reliably fast after.
 */
export function SlowNotice({
  active,
  message = "Still loading…",
  className,
}: {
  active: boolean;
  message?: string;
  className?: string;
}) {
  const show = useDelayedFlag(active, SLOW_DELAY_MS);
  if (!show) return null;

  return (
    <p
      // Announced politely so a screen reader hears it when it arrives, rather
      // than interrupting whatever is being read at the time.
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center gap-2 text-sm text-muted-foreground",
        className,
      )}
    >
      <Loader2 className="size-3.5 animate-spin" />
      {message}
    </p>
  );
}
